/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Types
import {
	TApiResponseCreateConversionFactor,
	TBodyCreateConversionFactor,
} from '../types';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import { TApiError } from '@global-types/api/api';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addConversionFactor = (
	body: TBodyCreateConversionFactor
): Promise<TApiResponseCreateConversionFactor> => {
	return apiClient.post('/conversion_factors', body);
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useAddConversionFactor = (
	page: number,
	size: number
) => {
	const { t } = useTranslation();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addConversionFactor,

		onSuccess: async () => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'conversionFactors.notifications.conversionFactorSuccessfullyAdded'
				),
				color: 'success',
			});
			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.conversionFactors.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
